import dayjs from 'dayjs';
import queryStringParser from 'query-string';

import {
  STEPPER_FLOWS,
  AI_EDITOR_QUERY_PARAM_KEYS,
} from 'components/ReportViewComponents/constants';

export const WORKER_FETCH_STATE = {
  IDLE: 'IDLE',
  FETCHING: 'FETCHING',
  IMPORTING: 'IMPORTING',
  READY: 'READY',
  FETCH_FAILED: 'FETCH_FAILED',
  IMPORT_FAILED: 'IMPORT_FAILED',
};

export const initialWorkerState = {
  changeId: null,
  fetchState: WORKER_FETCH_STATE.IDLE,
  recordsFetched: 0,
  totalRecords: 0,
};

/**
 * Generates a database name prefix for the current tab. This is so that two
 * databases in different tabs do not collide which can cause consistency issues.
 */
export function generateDatabaseNamePrefix({ tabId }) {
  return `__integrtr_dataflow-${tabId}-`;
}

/**
 * Function that parses the query url and returns the entity name from it
 *
 * @param {string} url the query url from which you want to extract the entity
 * @returns {string} the entity name
 */
export function extractEntityFromQueryString(url) {
  const match = url.match(/v2\/([^?]+)/);
  return match ? match[1] : null;
}

/**
 * Builds a compact `entity?$param=value&...` context string from the editor query URL,
 * keeping only AI_EDITOR_QUERY_PARAM_KEYS. Returns the bare entity when no params apply.
 *
 * @param {string} queryUrl - The full editor query URL from the report view state.
 * @param {string} baseEndpoint - The connection's OData base endpoint; URL must start with it.
 * @returns {string|null} The context string, or null when the URL is missing/not for this endpoint.
 */
export function extractEditorOdataQuery(queryUrl, baseEndpoint) {
  if (typeof queryUrl !== 'string' || !queryUrl.startsWith(`${baseEndpoint}/`)) {
    return null;
  }
  const entity = extractEntityFromQueryString(queryUrl);
  if (!entity) {
    return null;
  }
  const { query } = queryStringParser.parseUrl(queryUrl, { decode: true });
  const keptParams = AI_EDITOR_QUERY_PARAM_KEYS.reduce((params, key) => {
    if (query[key] !== undefined) {
      params[key] = query[key];
    }
    return params;
  }, {});
  const paramString = queryStringParser.stringify(keptParams, { encode: false, sort: false });
  return paramString ? `${entity}?${paramString}` : entity;
}

export const setStepperDialogPrimaryBtnText = (activeStep, processLength, finalStepBtnText) => {
  const finalStep = processLength - 1;
  if (activeStep === finalStep) {
    return finalStepBtnText;
  }
  return 'Next';
};

export const getStepperSteps = (stepperFlow) => {
  switch (stepperFlow) {
    case STEPPER_FLOWS.SYSTEM_CONFIGURATION.KEY:
      return STEPPER_FLOWS.SYSTEM_CONFIGURATION.STEPS;

    default:
      return null;
  }
};

export const EXPORT_FILE_PASSWORD_MAX_LENGTH = 32;

export const checkPasswordStrength = (currentString, isPasswordFieldRequired) => {
  const value = currentString ?? '';

  // Cap first so whitespace-only values over the limit get the max-length error,
  // not the empty-field path from the trim checks below.
  if (value.length > EXPORT_FILE_PASSWORD_MAX_LENGTH) {
    return {
      isPasswordValid: false,
      helperText: 'Max length allowed is 32 characters',
    };
  }

  // Optional: any value under the max length is accepted (including empty).
  if (!isPasswordFieldRequired) {
    return { isPasswordValid: true, helperText: '' };
  }

  // Required: empty check, then strength rules.
  if (value.trim() === '') {
    return { isPasswordValid: false, helperText: 'Password Field cannot be empty' };
  }

  switch (true) {
    case value.length < 8:
      return {
        isPasswordValid: false,
        helperText: 'Length of password should be at least 8 characters',
      };
    case !/[!@#$%^&*(),.?":{}|<>]/.test(value):
      return {
        isPasswordValid: false,
        helperText: 'Should contain at least 1 special character',
      };
    case !/\d/.test(value):
      return {
        isPasswordValid: false,
        helperText: 'Should contain at least 1 number',
      };
    case !/[A-Z]/.test(value):
      return {
        isPasswordValid: false,
        helperText: 'Should contain at least 1 uppercase letter',
      };
    case !/[a-z]/.test(value):
      return {
        isPasswordValid: false,
        helperText: 'Should contain at least 1 lowercase letter',
      };
    default:
      return { isPasswordValid: true, helperText: '' };
  }
};

export const groupByMultiplicity = (array) => {
  return array.reduce((groupedColumnsArr, item) => {
    const multiplicity = item.multiplicity !== undefined ? item.multiplicity : 'base';
    const group = groupedColumnsArr.find((eachGroup) => eachGroup.multiplicity === multiplicity);
    if (group) {
      group.items.push(item);
    } else {
      groupedColumnsArr.push({ multiplicity, items: [item] });
    }
    return groupedColumnsArr;
  }, []);
};

/**
 * Finds the default collection from an array of collections.
 *
 * The function prioritizes finding a collection based on the following rules:
 * 1. If a collection has 'is_default' set to `true`, it is returned.
 * 2. If a collection is named 'Default Collection', it is returned.
 * 3. If neither of the above conditions are satisfied, the first collection
 *    in the array is returned as a fallback.
 *
 * @param {Array<Object>} collections - The list of collection objects to search.

 * @returns {Object|undefined} - The default collection object if found, or the
 * first collection in the list as a fallback. Returns `undefined` if the input
 * array is empty.
 */
export const getDefaultCollection = (collections = []) => {
  return (
    collections.find(
      (collection) => collection.is_default || collection.name === 'Default Collection'
    ) || collections[0]
  );
};

/**
 * Determines whether an entity, extracted from a given query URL,
 * is already present in the report view bookmarks.
 *
 * This function builds a Set of all entity names extracted from the bookmarks
 * and checks if the entity from the provided `queryUrl` exists in that Set.
 *
 * @param {Array<Object>} reportViewBookmarks - An array of bookmark collections.
 *   Each collection should have a `bookmarks` array, where each bookmark contains
 *   a `query_string` property.
 * @param {string} queryUrl - The query URL representing the entity to check for.
 *
 * @returns {boolean} - Returns `true` if the entity extracted from `queryUrl`
 *   exists in the bookmarks, otherwise `false`.
 *
 */
export const isEntityBookmarked = (reportViewBookmarks = [], entityCode) => {
  // Set contains the names of all entities extracted from the bookmarks.
  const entitySet = new Set(
    reportViewBookmarks.flatMap((collection) =>
      collection.bookmarks.map((bookmark) => extractEntityFromQueryString(bookmark.query_string))
    )
  );

  return entitySet.has(entityCode);
};

/**
 * Filters and returns all bookmarks from the report view collections
 * that match the entity extracted from the given query URL.
 *
 * Each matching bookmark is enriched with its corresponding `collection_name`.
 *
 * @param {Array<Object>} reportViewBookmarks - An array of bookmark collections.
 *   Each collection should have a `name` and a `bookmarks` array property.
 * @param {string} queryUrl - The query URL whose entity will be matched against the bookmarks.
 *
 * @returns {Array<Object>} - An array of bookmarks that belong to the same entity as the one
 *   extracted from the `queryUrl`. Each bookmark includes a `collection_name` field.
 *
 */
export const getBookmarksByEntityCode = (reportViewBookmarks = [], entityCode) => {
  const entityMap = new Map();

  reportViewBookmarks.forEach(({ name: collectionName, bookmarks }) => {
    bookmarks.forEach((bookmark) => {
      const entity = extractEntityFromQueryString(bookmark.query_string);

      if (entity) {
        // bookmark with collection name
        const enrichedBookmark = { ...bookmark, collection_name: collectionName };
        if (!entityMap.has(entity)) {
          entityMap.set(entity, [enrichedBookmark]);
        } else {
          entityMap.get(entity).push(enrichedBookmark);
        }
      }
    });
  });

  return entityMap.get(entityCode) || [];
};

/**
 * Removes parentheses surrounding values following the 'in' operator in a string.
 * This only applies for SF systems working on top of OData v2 protocol.
 *
 * This is typically used to adjust OData v2 filter strings, e.g., converting:
 *   "$filter=field in ('a','b')" -> "$filter=field in 'a','b'"
 *
 * Example:
 *   removeParenthesesAfterIn("status in ('active','inactive')")
 *   // returns "status in 'active','inactive'"
 *
 * @param {string} str - The input string possibly containing 'in (...)' expressions.
 * @returns {string} The modified string with parentheses after 'in' removed.
 */

export function removeParenthesesAfterIn(str) {
  return str.replace(/ in \(([^)]*)\)/g, ' in $1');
}

/**
 * Decodes an OData request URL so query values use spaces instead of literal '+'.
 */
export function decodeODataRequestUrl(url) {
  if (!url) {
    return url;
  }

  try {
    const urlObj = new URL(url);
    const queryString = urlObj.searchParams.toString();
    const base = `${urlObj.origin}${urlObj.pathname}`;

    return queryString ? `${base}?${queryString}` : base;
  } catch {
    return removeParenthesesAfterIn(url);
  }
}

/**
 * Normalizes SF OData v2 `in` filter syntax on a query URL.
 * Decodes $filter before transforming (encoded URLs won't match the raw regex).
 */
export function normalizeSfODataQueryUrl(url, isSfSystem) {
  if (!isSfSystem || !url) {
    return url;
  }

  try {
    const urlObj = new URL(url);
    const filter = urlObj.searchParams.get('$filter');

    if (filter != null) {
      urlObj.searchParams.set('$filter', removeParenthesesAfterIn(filter));
    }

    return decodeODataRequestUrl(urlObj.toString());
  } catch {
    return removeParenthesesAfterIn(url);
  }
}

/**
 * Groups an array of conversations by date label relative to today.
 *
 * Conversations from today are grouped under "Today", those from yesterday
 * under "Yesterday", and all others under their formatted date (e.g. "Mar 20, 2026").
 *
 * @param {Array<Object>} conversations - The list of conversation objects.
 *   Each conversation should have a `created_at` property parseable by dayjs.
 * @returns {Object<string, Array<Object>>} An object keyed by date label,
 *   where each value is an array of conversations belonging to that date group.
 */
export function groupByDate(conversations) {
  const groups = {};
  const today = dayjs().startOf('day');
  const yesterday = today.subtract(1, 'day');

  (conversations || []).forEach((conversation) => {
    const date = dayjs(conversation.created_at);
    let label;
    if (date.isAfter(today)) {
      label = 'Today';
    } else if (date.isAfter(yesterday)) {
      label = 'Yesterday';
    } else {
      label = date.format('MMM D, YYYY');
    }
    if (!groups[label]) {
      groups[label] = [];
    }
    groups[label].push(conversation);
  });

  return groups;
}
